<template>
  <div class="engine-selector">
    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-2" />
    <v-alert v-if="loadError" dense text type="warning" class="mb-2 caption">
      {{ loadError }}
    </v-alert>

    <div
      v-for="entry in selectable"
      :key="entry.name"
      class="engine-selector__row"
      :class="{ 'engine-selector__row--locked': isLocked(entry.name) }"
      :data-engine="entry.name"
    >
      <v-switch
        :input-value="isChecked(entry.name)"
        :disabled="disabled || isLocked(entry.name)"
        :label="entry.label"
        :aria-label="entry.label"
        color="primary"
        hide-details
        dense
        inset
        class="engine-selector__switch mt-0"
        @change="onToggle(entry.name, $event)"
      />
      <div class="engine-selector__meta">
        <span v-if="entry.description" class="caption grey--text text--darken-1">
          {{ entry.description }}
        </span>
        <span
          v-for="hint in hintsFor(entry.name)"
          :key="`${entry.name}-${hint.code}`"
          class="engine-selector__hint caption"
          :class="
            hint.code === 'degraded_without' ? 'warning--text text--darken-2' : 'primary--text'
          "
        >
          <v-icon x-small :color="hint.code === 'degraded_without' ? 'warning' : 'primary'">
            {{ hint.code === 'degraded_without' ? 'mdi-alert-outline' : 'mdi-link-variant' }}
          </v-icon>
          {{ hint.message }}
        </span>
      </div>
    </div>

    <p v-if="!loading && selectable.length === 0" class="caption grey--text mb-0">
      No engines available for this source.
    </p>
    <p v-if="showEmptyError" class="engine-selector__error error--text caption mb-0" role="alert">
      Select at least one engine.
    </p>
  </div>
</template>

<script>
import VideosDataService from '@/services/VideosDataService';
import {
  DEFAULT_ENGINE_CATALOGUE,
  applySelection,
  defaultSelection,
  normalizeCatalogue,
  selectableEngines,
} from '@/utils/engineRules';

/**
 * Per-run engine switches (M3 contract 9).
 *
 * `value` (v-model) is the list of engines the user explicitly requested; dependencies are
 * shown checked and locked ("required by Coherence") without being written back to `value`, so
 * the backend can report them as `engines_auto_added`. The `change` event carries the full
 * `applySelection` result ({ engines, requested, auto_added, warnings, valid, error }).
 *
 * The catalogue comes from `GET videos/engines` unless the parent passes `catalogue`; when the
 * request fails the static DEFAULT_ENGINE_CATALOGUE is used and a warning is shown.
 */
export default {
  name: 'EngineSelector',
  props: {
    value: { type: Array, default: () => [] },
    sourceType: { type: String, required: true },
    catalogue: { type: Array, default: null },
    disabled: { type: Boolean, default: false },
    /** Select every selectable engine when `value` is empty on first load. */
    defaultAll: { type: Boolean, default: true },
  },
  data() {
    return {
      loading: false,
      loadError: '',
      loadedCatalogue: [],
      touched: false,
    };
  },
  computed: {
    activeCatalogue() {
      if (Array.isArray(this.catalogue) && this.catalogue.length) {
        return normalizeCatalogue(this.catalogue);
      }
      return this.loadedCatalogue;
    },
    selectable() {
      return selectableEngines(this.activeCatalogue, this.sourceType);
    },
    selection() {
      return applySelection(this.activeCatalogue, this.sourceType, this.value);
    },
    lockedBy() {
      const locked = {};
      this.selection.warnings
        .filter((warning) => warning.code === 'required_by')
        .forEach((warning) => {
          locked[warning.engine] = warning.related[0];
        });
      return locked;
    },
    showEmptyError() {
      return (
        this.touched &&
        !this.loading &&
        this.selectable.length > 0 &&
        this.selection.engines.length === 0
      );
    },
  },
  watch: {
    selection: {
      immediate: true,
      handler(result) {
        this.$emit('change', result);
      },
    },
    sourceType() {
      this.applyDefaultIfEmpty();
    },
  },
  created() {
    if (Array.isArray(this.catalogue) && this.catalogue.length) {
      this.applyDefaultIfEmpty();
      return;
    }
    this.loadCatalogue();
  },
  methods: {
    async loadCatalogue() {
      this.loading = true;
      this.loadError = '';
      try {
        const response = await VideosDataService.getEngines();
        const body = response?.data?.data ?? response?.data ?? {};
        const engines = normalizeCatalogue(body.engines || body);
        if (!engines.length) throw new Error('empty_catalogue');
        this.loadedCatalogue = engines;
      } catch (error) {
        this.loadedCatalogue = normalizeCatalogue(DEFAULT_ENGINE_CATALOGUE);
        this.loadError = 'Engine catalogue unavailable; showing the default engines.';
      } finally {
        this.loading = false;
        this.$emit('catalogue-loaded', this.activeCatalogue);
        this.applyDefaultIfEmpty();
      }
    },
    applyDefaultIfEmpty() {
      if (!this.defaultAll || this.touched) return;
      if (Array.isArray(this.value) && this.value.length) return;
      const defaults = defaultSelection(this.activeCatalogue, this.sourceType);
      if (defaults.length) this.$emit('input', defaults);
    },
    isChecked(name) {
      return this.selection.engines.includes(name);
    },
    isLocked(name) {
      return Object.prototype.hasOwnProperty.call(this.lockedBy, name);
    },
    hintsFor(name) {
      return this.selection.warnings.filter(
        (warning) =>
          warning.engine === name &&
          (warning.code === 'required_by' || warning.code === 'degraded_without')
      );
    },
    onToggle(name, checked) {
      this.touched = true;
      const current = Array.isArray(this.value) ? this.value.filter((item) => item !== name) : [];
      const next = checked ? [...current, name] : current;
      this.$emit('input', next);
    },
  },
};
</script>

<style scoped>
.engine-selector__row {
  display: flex;
  flex-direction: column;
  padding: 6px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.engine-selector__row:last-of-type {
  border-bottom: none;
}
.engine-selector__row--locked {
  opacity: 0.85;
}
.engine-selector__meta {
  display: flex;
  flex-direction: column;
  padding-left: 52px;
}
.engine-selector__hint {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
</style>
